<!-- START TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy -->
<template>
    <div
        :class="billingCadenceOverlayClass"
        id="billing-cadence-modal-overlay"
        role="presentation"
        :aria-hidden="overlayAriaHidden"
        @click="handleCloseCadenceModalCta">
        <div
            :class="billingCadenceModalClass"
            id="billing-cadence-modal"
            ref="modalPanel"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="dialogHeadingId"
            @click.stop
            @keydown="handleDialogKeydown">
            <div class="cadence-image" :style="cadenceImageBackgroundStyle"></div>

            <div class="cadence-modal-content">
                <h2 :id="dialogHeadingId">{{ getHeading }}</h2>

                <div
                    class="cadence-radiogroup"
                    role="radiogroup"
                    :aria-labelledby="dialogHeadingId">
                    <radiobox-elm
                        id="billing-cadence-monthly"
                        name="billing-cadence"
                        v-model="billingCadencePicked"
                        :value="getProductMonthly">
                        <PlanCoupon
                            v-if="monthlyCouponString"
                            :hasValidCoupon="true"
                            :couponString="monthlyCouponString"
                            :classList="cadenceModalCouponClassList" />
                        <div class="radio-text">
                            <div class="radio-heading">{{ getMonthlyHeading }}</div>
                            <div v-if="monthlyCadenceEftTrialCopy" class="cadence-eft-trial-copy">
                                {{ monthlyCadenceEftTrialCopy }}
                            </div>
                            <div class="plan-price-copy">
                                <span>{{ monthlyPlanPriceCopy }}</span>
                            </div>
                            <div v-if="monthlyHasCoupon" class="cadence-after-promo">
                                <span>{{ monthlyAfterPromoDescription }}</span>
                            </div>
                        </div>
                    </radiobox-elm>
                    <radiobox-elm
                        id="billing-cadence-annually"
                        name="billing-cadence"
                        v-model="billingCadencePicked"
                        :value="getProductAnnual">
                        <PlanCoupon
                            v-if="annualCouponString"
                            :hasValidCoupon="true"
                            :couponString="annualCouponString"
                            :classList="cadenceModalCouponClassList" />
                        <div class="radio-text">
                            <div class="radio-heading">{{ getAnnualHeading }}</div>
                            <div v-if="annualCadenceEftTrialCopy" class="cadence-eft-trial-copy">
                                {{ annualCadenceEftTrialCopy }}
                            </div>
                            <div class="plan-price-copy">
                                <span>{{ annualPlanPriceCopy }}</span>
                            </div>
                            <div v-if="annualHasCoupon" class="cadence-after-promo">
                                <span>{{ annualAfterPromoDescription }}</span>
                            </div>
                            <div
                                v-else-if="selectedAnnualPlan && !annualHasCoupon"
                                class="annual-effective-copy">
                                {{ annualMonthlyEquivalentCopy }}
                            </div>
                        </div>
                    </radiobox-elm>
                </div>

                <ButtonCta :buttonConfig="billingCadenceCtaConfig"></ButtonCta>
            </div>
        </div>
    </div>
</template>

<script>
    import PlanCoupon from 'aa/vue/components/PlanCoupon';
    import RadioboxElm from 'atoms/RadioboxElm';
    import ButtonCta from 'atoms/ButtonCta';
    import { ACTION_NAMES, tracking } from 'services/Tracking';
    import Product from 'aa/vue/models/Product';
    import { mapGetters, mapState } from 'vuex';
    import Breakpoints from 'helpers/breakpoints';
    import { FTC_COMPLIANCE_PICKAPLAN_ENABLED } from 'aa/helpers/featureConstants';
    import {
        getOfferPriceString,
        getDurationString,
        getAfterPromoString,
    } from 'aa/vue/helpers/coupon.helpers';
    import { getLocale } from 'services/LocalizationService';

    export default {
        name: 'BillingCadenceModal',
        mixins: [],
        components: {
            PlanCoupon,
            RadioboxElm,
            ButtonCta,
        },
        props: {
            monthlyOnly: {
                type: Boolean,
                default: false,
            },
        },
        data: function () {
            return {
                isBillingCadenceModal: this.$store.getters['variant/isBillingCadenceModal'],
                billingCadencePicked: Product.TYPE_MONTHLY.toLowerCase(),
                breakpointHelper: new Breakpoints(),
                eventList: ['wheel', 'scroll', 'touchmove'],
                // Stable id on the visible <h2> only — dialog aria-labelledby + radiogroup labelling
                dialogHeadingId: 'billing-cadence-dialog-heading',
                previousActiveElement: null,
            };
        },
        computed: {
            ...mapGetters({
                featureFlagIsActive: 'featureFlags/isActive',
                cmsImageBaseURL: 'cmsImageBaseURL',
                selectedPlan: 'plan/getSelectedPlan',
                selectedAnnualPlan: 'plan/getSelectedAnnualPlan',
                billingCadenceModalOpen: 'plan/getBillingCadenceModalOpen',
                annualSelectedAndMonthlyAvailable: 'plan/annualSelectedAndMonthlyAvailable',
            }),
            ftcCompliancePickAPlanEnabled() {
                return this.featureFlagIsActive(FTC_COMPLIANCE_PICKAPLAN_ENABLED);
            },
            ...mapState('pageAttributes', {
                pageAttributes: (state) => state.attributes,
            }),
            effectivePlanForMonthlyColumn() {
                if (!this.monthlyOnly) {
                    return this.selectedPlan;
                }
                if (!this.selectedPlan) {
                    return null;
                }
                if (this.selectedPlan.planType === Product.TYPE_MONTHLY) {
                    return this.selectedPlan;
                }
                const matches = this.annualSelectedAndMonthlyAvailable;
                return matches?.length ? matches[0] : this.selectedPlan;
            },
            selectedAnnualPlanRawPrice() {
                return this.selectedAnnualPlan ? this.selectedAnnualPlan.rawPrice : '';
            },
            selectedAnnualPlanMonthlyPrice() {
                const monthAbbr = getLocale('month_abbr', {}, 'mo');
                return this.selectedAnnualPlan
                    ? this.selectedAnnualPlan.annualPlanMonthlyPriceWithUnit(monthAbbr)
                    : '';
            },
            getProductMonthly() {
                return Product.TYPE_MONTHLY.toLowerCase();
            },

            getProductAnnual() {
                return Product.TYPE_ANNUAL.toLowerCase();
            },
            billingCadenceModalClass() {
                let classList;

                classList = {
                    'billing-cadence-modal': true,
                    open: this.billingCadenceModalOpen,
                };

                return classList;
            },
            billingCadenceOverlayClass() {
                let classList;

                classList = {
                    'billing-cadence-modal-overlay': true,
                    open: this.billingCadenceModalOpen,
                };

                return classList;
            },
            overlayAriaHidden() {
                return this.billingCadenceModalOpen ? null : 'true';
            },
            closeButtonAriaLabel() {
                return getLocale('close', {}, 'Close');
            },
            billingCadenceCtaConfig() {
                const displayText = (
                    this.pageAttributes?.aa_pap_button_text_modal ?? ''
                ).toUpperCase();
                return {
                    styleClass: 'cadence-cta',
                    displayText,
                    ariaLabel: displayText,
                    event: this.handleClickCadenceModalCta,
                };
            },

            getHeading() {
                return this.pageAttributes.cadence_overlay_heading;
            },
            getMonthlyHeading() {
                return this.pageAttributes.cadence_overlay_monthly;
            },
            getAnnualHeading() {
                return this.pageAttributes.cadence_overlay_annual;
            },
            cadenceImageBackgroundStyle() {
                const path = this.pageAttributes.billing_cadence_background_image;
                if (!path || !this.cmsImageBaseURL) {
                    return {};
                }
                const fullUrl = this.cmsImageBaseURL + path;
                return {
                    backgroundImage: `url(${JSON.stringify(fullUrl)})`,
                };
            },
            monthlyCouponString() {
                const plan = this.effectivePlanForMonthlyColumn;
                if (plan?.hasValidCoupon()) {
                    return plan.getCouponString() || '';
                }
            },
            annualCouponString() {
                if (this.selectedAnnualPlan?.hasValidCoupon()) {
                    return this.selectedAnnualPlan.getCouponString() || '';
                }
            },
            cadenceModalCouponClassList() {
                return ['plan-coupon', 'cadence-modal-coupon'];
            },
            monthlyHasCoupon() {
                const plan = this.effectivePlanForMonthlyColumn;
                return plan && plan.isCouponValidAndAllowed();
            },
            annualHasCoupon() {
                return this.selectedAnnualPlan && this.selectedAnnualPlan.isCouponValidAndAllowed();
            },
            monthlyCadenceEftTrialCopy() {
                const plan = this.effectivePlanForMonthlyColumn;
                if (!plan || typeof plan.isEftCoupon !== 'function') {
                    return '';
                }
                if (!plan.isEftCoupon() || !plan.couponString) {
                    return '';
                }
                return this.formatExtendedEftCouponString(plan);
            },
            annualCadenceEftTrialCopy() {
                const plan = this.selectedAnnualPlan;
                if (!plan || typeof plan.isEftCoupon !== 'function') {
                    return '';
                }
                if (!plan.isEftCoupon() || !plan.couponString) {
                    return '';
                }
                return this.formatExtendedEftCouponString(plan);
            },
            monthlyPlanPriceCopy() {
                const plan = this.effectivePlanForMonthlyColumn;
                if (!plan) return '';
                const monthAbbr = getLocale('month_abbr', {}, 'mo');
                if (plan.isCouponValidAndAllowed()) {
                    const offerString = getOfferPriceString(
                        plan.currencySymbol,
                        plan.couponDiscount.priceSubtotal,
                        plan.priceUnit,
                        monthAbbr,
                        plan.config.recurlyCoupon,
                        this.ftcCompliancePickAPlanEnabled,
                        false,
                        plan.currencySubunits,
                    );
                    const durationString = getDurationString(
                        plan.config.recurlyCoupon,
                        plan.priceUnit,
                        false,
                        this.ftcCompliancePickAPlanEnabled,
                    );
                    return this.normalizeCouponPlanPriceCadenceSlashes(
                        `${offerString}${durationString}`,
                    );
                }
                return `$${plan.rawPrice}/${monthAbbr}`;
            },
            monthlyAfterPromoDescription() {
                const plan = this.effectivePlanForMonthlyColumn;
                if (!plan || !plan.isCouponValidAndAllowed()) return '';
                const shortenedTerm = getLocale('month_abbr', {}, 'mo');
                let hasTrial = plan.trial;
                try {
                    if (this.$store.getters['user/isNoTrialUser'] === true) hasTrial = false;
                } catch (e) {}
                return getAfterPromoString(
                    plan.config.recurlyCoupon,
                    plan.currencySymbol,
                    plan.rawPrice,
                    shortenedTerm,
                    this.ftcCompliancePickAPlanEnabled,
                    hasTrial,
                );
            },
            annualPlanPriceCopy() {
                const plan = this.selectedAnnualPlan;
                if (!plan) return '';
                const yearAbbr = getLocale('year_abbr', {}, 'yr');
                if (plan.isCouponValidAndAllowed()) {
                    const offerString = getOfferPriceString(
                        plan.currencySymbol,
                        plan.couponDiscount.priceSubtotal,
                        plan.priceUnit,
                        yearAbbr,
                        plan.config.recurlyCoupon,
                        this.ftcCompliancePickAPlanEnabled,
                        true,
                        plan.currencySubunits,
                    );
                    const durationString = getDurationString(
                        plan.config.recurlyCoupon,
                        plan.priceUnit,
                        true,
                        this.ftcCompliancePickAPlanEnabled,
                    );
                    return this.normalizeCouponPlanPriceCadenceSlashes(
                        `${offerString}${durationString}`,
                    );
                }
                return `$${this.selectedAnnualPlanRawPrice}/${yearAbbr}`;
            },
            annualAfterPromoDescription() {
                const plan = this.selectedAnnualPlan;
                if (!plan || !plan.isCouponValidAndAllowed()) return '';
                const shortenedTerm = getLocale('year_abbr', {}, 'yr');
                let hasTrial = plan.trial;
                try {
                    if (this.$store.getters['user/isNoTrialUser'] === true) hasTrial = false;
                } catch (e) {}
                return getAfterPromoString(
                    plan.config.recurlyCoupon,
                    plan.currencySymbol,
                    plan.rawPrice,
                    shortenedTerm,
                    this.ftcCompliancePickAPlanEnabled,
                    hasTrial,
                );
            },
            annualMonthlyEquivalentCopy() {
                const plan = this.selectedAnnualPlan;
                if (!plan) return '';
                const monthAbbr = getLocale('month_abbr', {}, 'mo');
                const monthlyWithUnit = plan.annualPlanMonthlyPriceWithUnit(monthAbbr);
                try {
                    const format = this.pageAttributes?.aa_pap_annual_plan_monthly_price_format;
                    if (!format) return monthlyWithUnit;
                    return format.replace('%MONTHLY_PRICE_WITH_UNIT%', monthlyWithUnit);
                } catch (e) {
                    return monthlyWithUnit;
                }
            },
        },
        methods: {
            formatExtendedEftCouponString(plan) {
                const couponFormat =
                    this.pageAttributes?.aa_pap_extended_free_trial_coupon_string || '';
                return couponFormat.replace('%s', plan?.couponString ?? '');
            },
            normalizeCouponPlanPriceCadenceSlashes(line) {
                if (!line) {
                    return line;
                }
                const monthAbbr = getLocale('month_abbr', {}, 'mo');
                const yearAbbr = getLocale('year_abbr', {}, 'yr');
                return line
                    .replace(/\/month\b/gi, `/${monthAbbr}`)
                    .replace(/\/year\b/gi, `/${yearAbbr}`);
            },
            syncOverlayInert() {
                const el = document.getElementById('billing-cadence-modal-overlay');
                if (el && 'inert' in el) {
                    el.inert = !this.billingCadenceModalOpen;
                }
            },
            getTabbableElements(root) {
                if (!root || !root.querySelectorAll) {
                    return [];
                }
                const selector = [
                    'button:not([disabled]):not([tabindex="-1"])',
                    'input:not([disabled]):not([tabindex="-1"])',
                    'select:not([disabled]):not([tabindex="-1"])',
                    'textarea:not([disabled]):not([tabindex="-1"])',
                    'a[href]:not([tabindex="-1"])',
                    '[tabindex]:not([tabindex="-1"])',
                ].join(', ');
                return Array.from(root.querySelectorAll(selector)).filter((el) => {
                    return el instanceof HTMLElement;
                });
            },
            focusInitialElement() {
                const panel = this.$refs.modalPanel;
                if (!panel) {
                    return;
                }
                const checked = panel.querySelector(
                    'input[type="radio"][name="billing-cadence"]:checked',
                );
                const firstRadio = panel.querySelector(
                    'input[type="radio"][name="billing-cadence"]',
                );
                const target = checked || firstRadio;
                if (target && typeof target.focus === 'function') {
                    target.focus();
                }
            },
            restoreFocus() {
                const el = this.previousActiveElement;
                this.previousActiveElement = null;
                if (el && typeof el.focus === 'function' && document.body.contains(el)) {
                    el.focus();
                }
            },
            handleDialogKeydown(e) {
                if (!this.billingCadenceModalOpen) {
                    return;
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleCloseCadenceModalCta();
                    return;
                }
                if (e.key !== 'Tab') {
                    return;
                }
                const panel = this.$refs.modalPanel;
                if (!panel) {
                    return;
                }
                const focusables = this.getTabbableElements(panel);
                if (focusables.length === 0) {
                    return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    }
                } else if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            },
            moveBillingCadenceModalToBody() {
                const billingCadenceOverlay = document.getElementById(
                    'billing-cadence-modal-overlay',
                );
                const bodyElement = document.body;

                if (billingCadenceOverlay && bodyElement) {
                    bodyElement.appendChild(billingCadenceOverlay);
                }
            },
            handleCloseCadenceModalCta(includeTracking = true, popHistory = true) {
                if (includeTracking) {
                    tracking.trackAction(ACTION_NAMES.TRACK_PICK_PLAN_MODAL_ABANDON);
                }
                const wasOpen = this.billingCadenceModalOpen;
                this.$store.commit('plan/setBillingCadenceModalOpen', false);

                // Only pop the history entry we pushed when the user explicitly closed (X or overlay).
                if (popHistory && wasOpen && window.history.state?.modal === 'billingCadence') {
                    window.history.back();
                }
            },
            handleClickCadenceModalCta() {
                const isAnnual = this.billingCadencePicked === Product.TYPE_ANNUAL;
                if (this.monthlyOnly) {
                    if (isAnnual && this.selectedAnnualPlan) {
                        this.$store.dispatch('plan/updateSelectedPlan', this.selectedAnnualPlan);
                    } else if (!isAnnual && this.effectivePlanForMonthlyColumn) {
                        this.$store.dispatch(
                            'plan/updateSelectedPlan',
                            this.effectivePlanForMonthlyColumn,
                        );
                    }
                } else if (isAnnual && this.selectedAnnualPlan) {
                    this.$store.dispatch('plan/updateSelectedPlan', this.selectedAnnualPlan);
                }
                this.handleCloseCadenceModalCta(false, false);

                const planForTracking = this.monthlyOnly
                    ? isAnnual
                        ? this.selectedAnnualPlan
                        : this.effectivePlanForMonthlyColumn
                    : isAnnual && this.selectedAnnualPlan
                      ? this.selectedAnnualPlan
                      : this.selectedPlan;
                if (planForTracking) {
                    tracking.trackAction('trackBillingCycleSelection', {
                        ctaText: this.billingCadenceCtaConfig.displayText,
                        pickPlanType: planForTracking.planTitle,
                        pickPlanSku: planForTracking.recurlyCode,
                        productPricingPlan: planForTracking.planType,
                    });
                }
                this.$emit('handleSubmit');
            },
            isFullScreenModalBreakpoint() {
                return (
                    window.innerWidth <= this.breakpointHelper.getBreakpoint('tabletPortrait').max
                );
            },
            addEvents() {
                this.eventList.forEach((event) => {
                    document.body.addEventListener(event, this.preventScroll, {
                        passive: false,
                    });
                });
            },
            removeEvents() {
                this.eventList.forEach((event) => {
                    document.body.removeEventListener(event, this.preventScroll, {
                        passive: false,
                    });
                });
            },
            preventScroll(e) {
                const panel = this.$refs.modalPanel;
                if (
                    panel instanceof HTMLElement &&
                    e.target instanceof Node &&
                    panel.contains(e.target)
                ) {
                    return;
                }
                e.preventDefault();
            },
            handlePopState(event) {
                if (this.billingCadenceModalOpen) {
                    this.handleCloseCadenceModalCta(false, false);

                    window.history.pushState({ modal: 'billingCadence' }, null);
                }
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.moveBillingCadenceModalToBody();
                this.syncOverlayInert();
            });

            window.addEventListener('popstate', this.handlePopState);
        },
        watch: {
            billingCadenceModalOpen: function (n, o) {
                // When modal opens, push a history state so back button closes modal instead of navigating away
                if (n && !o) {
                    this.previousActiveElement =
                        document.activeElement instanceof HTMLElement
                            ? document.activeElement
                            : null;
                    window.history.pushState({ modal: 'billingCadence' }, null);
                }

                this.$nextTick(() => {
                    this.syncOverlayInert();
                    if (n && !o) {
                        this.focusInitialElement();
                    }
                    if (!n && o) {
                        this.restoreFocus();
                    }
                });

                if (this.isFullScreenModalBreakpoint() && n) {
                    this.addEvents();
                }

                if (!n) {
                    this.removeEvents();
                }
            },
        },
        destroyed() {
            window.removeEventListener('popstate', this.handlePopState);
        },
    };
</script>
<!-- END TEST PLOCTOPLUS-2947 Annual Plan Emphasis on PAP Adjusted Pricing Display and Hierarchy -->
